import { giphyBlack } from '@giphy/js-brand'
import { GifsResult } from '@giphy/js-fetch-api'
import { ForwardedRef, forwardRef, ReactNode, useImperativeHandle, useRef, useState } from 'react'
import { gifFromMediaSprite } from '../../utils/media/gif'
import { ArtboardRef } from '../artboard/types/refs'
import UgeContextManager from './contexts'
import Editor from './editor'
import useSourceInfo from './hooks/use-source-info'
import Navigation from './navigation'
import Overlay from './overlay'
import Preview from './preview'
import { CloseButton, Container, Loader, Panel, PanelContent } from './style'

type Props = {
    apiKey: string
    children?: ReactNode
    className?: string
    source: File | string
    editorToolButtons?: ReactNode
    fetchFavorites?: (offset: number) => Promise<GifsResult>
    onClose?: () => void
}

export type UGERef = {
    getFrames: () => HTMLCanvasElement[]
    getGif: () => Promise<File | null>
}

/**
 Universal GIPHY Editor

██╗   ██╗ ██████╗ ███████╗
██║   ██║██╔════╝ ██╔════╝
██║   ██║██║  ███╗█████╗  
██║   ██║██║   ██║██╔══╝  
╚██████╔╝╚██████╔╝███████╗
 ╚═════╝  ╚═════╝ ╚══════╝
 */
const UGE = (
    { apiKey, children, className, editorToolButtons, source, fetchFavorites, onClose }: Props,
    ref: ForwardedRef<UGERef>
) => {
    const artboardRef = useRef<ArtboardRef>(null)
    const sourceInfo = useSourceInfo(source)
    const [isGeneratingGif, setIsGeneratingGif] = useState<boolean>(false)

    const getTime = (): number => {
        return artboardRef.current?.getTime() || 0
    }

    const goToTime = (seconds: number) => {
        artboardRef.current?.goToTime(seconds)
    }

    useImperativeHandle(ref, () => ({
        getFrames: () => {
            if (!artboardRef.current) {
                return []
            }

            return artboardRef.current.getFrames()
        },
        getGif: async () => {
            if (!artboardRef.current) {
                return null
            }

            setIsGeneratingGif(true)

            const sprite = artboardRef.current.getSprite()
            const gif = await gifFromMediaSprite(sprite)

            setIsGeneratingGif(false)

            return gif
        },
    }))

    if (!sourceInfo) {
        return <Loader type="blocks" size="large" />
    }

    return (
        <UgeContextManager
            apiKey={apiKey}
            fetchFavorites={fetchFavorites}
            getTime={getTime}
            goToTime={goToTime}
            source={source}
            sourceInfo={sourceInfo}
        >
            <Container className={className}>
                {onClose && <CloseButton color={giphyBlack} icon="close" size="large" onClick={onClose} />}
                <Panel>
                    <PanelContent>
                        <Preview isGeneratingGif={isGeneratingGif} ref={artboardRef}>
                            <Overlay />
                        </Preview>
                        <Editor>{editorToolButtons}</Editor>
                    </PanelContent>
                    {children}
                </Panel>
                <Navigation />
            </Container>
        </UgeContextManager>
    )
}

export default forwardRef(UGE)
